import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { PublicSchoolDto } from '@motoschool/shared';
import { api } from '../lib/api';
import { cn } from '../components/ui';
import { SchoolName } from '../components/SchoolName';
import { Logo } from '../components/Logo';
import { MotoIcon, type MotoIconName } from '../components/MotoIcons';
import { ContactSheet } from '../components/ContactSheet';
import { usePageMeta } from '../lib/seo';

/* --------------------------------------------------------------------------
 * Страницы под отдельные запросы: «категория A», «категория A1», «с нуля».
 *
 * Одна главная не может отвечать на три разных вопроса сразу: человек,
 * который ищет обучение на A1, хочет прочитать про A1, а не про школу вообще.
 * Поэтому у каждой страницы свой заголовок, свой текст и свой адрес.
 *
 * Тексты обещают только то, что школа действительно делает: занятия один на
 * один, упражнения на площадке, программа под уровень. Требований закона,
 * сроков и гарантий тут нет — их сначала должен подтвердить владелец школы,
 * а неподтверждённое обещание на сайте дороже любого места в выдаче.
 * -------------------------------------------------------------------------- */

interface Course {
  slug: string;
  /** Заголовок первого экрана: вторая половина выделяется цветом. */
  h1: { plain: string; accent: string };
  metaTitle: string;
  metaDescription: string;
  /** Короткое описание для ссылок с других страниц курсов. */
  short: string;
  intro: string;
  blocks: Array<{ icon: MotoIconName; title: string; text: string }>;
  outro: string;
}

export const COURSES: Course[] = [
  {
    slug: 'kategoriya-a',
    h1: { plain: 'Обучение на категорию', accent: 'A' },
    metaTitle: 'Обучение на категорию A в Алматы — мотошкола FREEDOM BIKER',
    metaDescription:
      'Категория A: занятия на мотоцикле с инструктором один на один на площадке ' +
      'в Алматы. Упражнения площадки, работа над техникой, программа под ваш уровень.',
    short: 'Мотоцикл без ограничений по объёму: техника управления и упражнения площадки.',
    intro:
      'Категория A — это мотоцикл без ограничений по объёму двигателя, и требования ' +
      'к управлению у него выше, чем у лёгкой техники. На занятиях разбираем посадку ' +
      'и органы управления, трогание и остановку, движение на малой скорости и ' +
      'упражнения площадки — до того, как они начнут получаться уверенно.',
    blocks: [
      {
        icon: 'moto',
        title: 'Один на один',
        text: 'Всё занятие инструктор работает только с вами: на площадке нет группы и очереди между заездами.',
      },
      {
        icon: 'clipboard',
        title: 'Упражнения площадки',
        text: 'Змейка, восьмёрка, габаритный коридор, разгон и торможение, старт на подъёме — по одному, а не всё подряд.',
      },
      {
        icon: 'rotate',
        title: 'После перерыва',
        text: 'Если права есть, а практики давно нет, занятие строится вокруг возврата навыка, а не вокруг азов.',
      },
    ],
    outro:
      'Сколько занятий понадобится, зависит от начального уровня: после первого ' +
      'инструктор скажет, на что уйдёт время именно у вас.',
  },
  {
    slug: 'kategoriya-a1',
    h1: { plain: 'Обучение на категорию', accent: 'A1' },
    metaTitle: 'Обучение на категорию A1 в Алматы — мотошкола FREEDOM BIKER',
    metaDescription:
      'Категория A1: обучение вождению лёгкого мотоцикла в Алматы. Занятия один ' +
      'на один с инструктором на площадке, программа под ваш уровень.',
    short: 'Лёгкий мотоцикл: с него чаще всего начинают знакомство с техникой.',
    intro:
      'A1 — лёгкий мотоцикл, и с него чаще всего начинают. Занятие строится с нуля: ' +
      'знакомство с техникой, посадка, сцепление и газ, трогание и остановка, ' +
      'а дальше — движение на малой скорости и упражнения площадки.',
    blocks: [
      {
        icon: 'helmet',
        title: 'Без опыта',
        text: 'Если раньше не садились за руль мотоцикла, начнём со знакомства с техникой — это нормальная точка старта.',
      },
      {
        icon: 'road',
        title: 'Спокойный темп',
        text: 'К следующему упражнению переходим, когда получается предыдущее, а не по расписанию курса.',
      },
      {
        icon: 'calendar',
        title: 'Время под вас',
        text: 'Занятия подбираются под ваш график, а не под общий поток.',
      },
    ],
    outro:
      'Что именно нужно для получения прав категории A1 и с какого возраста — ' +
      'уточните у инструктора: он подскажет актуальные требования.',
  },
  {
    slug: 's-nulya',
    h1: { plain: 'Научиться ездить', accent: 'с нуля' },
    metaTitle: 'Научиться ездить на мотоцикле с нуля — Алматы, FREEDOM BIKER',
    metaDescription:
      'Обучение вождению мотоцикла с нуля в Алматы: первое занятие со знакомства ' +
      'с техникой, дальше площадка. Инструктор работает с вами один на один.',
    short: 'Первое занятие: знакомство с мотоциклом, трогание и остановка.',
    intro:
      'Первое занятие начинается не с газа, а со знакомства с мотоциклом: где что ' +
      'находится, как он ведёт себя на малой скорости, что делает сцепление. ' +
      'Дальше — трогание, остановка и первые упражнения на площадке.',
    blocks: [
      {
        icon: 'helmet',
        title: 'Страшно — это нормально',
        text: 'Первое занятие проходит на площадке: без движения по городу и без машин рядом.',
      },
      {
        icon: 'moto',
        title: 'Своя техника не нужна',
        text: 'На чём проходит занятие и что взять с собой, инструктор расскажет при подтверждении записи.',
      },
      {
        icon: 'bolt',
        title: 'Начать просто',
        text: 'Звонок или сообщение — инструктор ответит и назначит первое занятие.',
      },
    ],
    outro:
      'Дальше программа зависит от того, к чему идёте: уверенная езда для себя, ' +
      'подготовка к экзамену или возвращение в седло после перерыва.',
  },
];

const PANEL = 'rounded-card border border-white/10 bg-white/[0.04] p-5 transition';

const CTA_BASE =
  'inline-flex min-h-13 items-center justify-center gap-2 rounded-xl px-6 text-sm font-semibold uppercase tracking-wide transition ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent';

/**
 * Адрес страницы приходит пропом, а не из параметров маршрута: маршруты
 * заданы точными путями (`/kategoriya-a`), и `:slug` в них просто нет —
 * ловить всё подряд одним `/:slug` нельзя, он перехватил бы и `/login`.
 */
export function CoursePage({ slug }: { slug: string }): React.JSX.Element {
  const course = COURSES.find((item) => item.slug === slug);
  const [isContacting, setIsContacting] = useState(false);

  const { data: school } = useQuery({
    queryKey: ['public', 'school'],
    queryFn: () => api.get<PublicSchoolDto>('/public/school', undefined, { anonymous: true }),
  });

  usePageMeta({
    title: course?.metaTitle ?? '',
    description: course?.metaDescription ?? '',
    path: `/${slug}`,
  });

  // Чужой адрес вида /kategoriya-b — на главную, а не на пустую страницу
  if (!course) return <Navigate to="/" replace />;

  return (
    <div className="min-h-dvh bg-night text-white">
      <header className="safe-top absolute inset-x-0 z-20 px-4 pb-3">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
          <Link to="/" className="flex min-w-0 items-center gap-2">
            <Logo className="size-9" />
            <SchoolName name={school?.name ?? 'Мотошкола'} className="truncate text-sm" />
          </Link>
        </div>
      </header>

      <section className="grain relative overflow-hidden px-4 pt-28 pb-16 sm:pt-32 sm:pb-20">
        {/* Тот же асфальт с фарой сверху, что и на главной */}
        <div
          aria-hidden
          className="absolute inset-0 bg-night"
          style={{
            backgroundImage:
              'radial-gradient(ellipse 80% 55% at 50% -12%, rgba(244,196,48,0.18), transparent 68%)',
          }}
        />

        <div className="relative mx-auto max-w-3xl">
          <span className="rise inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-accent">
            <span aria-hidden className="size-1.5 rounded-full bg-accent" />
            Мотошкола в Алматы
          </span>

          <h1 className="rise mt-6 text-4xl font-bold uppercase leading-[1.02] tracking-tight text-balance sm:text-5xl">
            {course.h1.plain} <span className="text-accent">{course.h1.accent}</span>
          </h1>

          <p className="rise mt-6 max-w-xl text-base leading-relaxed text-white/60 sm:text-lg">
            {course.intro}
          </p>

          <button
            type="button"
            onClick={() => setIsContacting(true)}
            className={cn(
              CTA_BASE,
              'rise mt-8 bg-accent text-night shadow-lg shadow-accent/20 hover:bg-accent-600 active:scale-[0.99]',
            )}
          >
            Связаться
            <MotoIcon name="arrow" className="size-4" />
          </button>
        </div>
      </section>

      <main className="mx-auto max-w-5xl space-y-16 px-4 pb-20">
        <section>
          <div className="grid gap-3 sm:grid-cols-3">
            {course.blocks.map((block) => (
              <div key={block.title} className={cn(PANEL, 'h-full hover:border-accent/40')}>
                <MotoIcon name={block.icon} className="size-7 text-accent" />
                <p className="mt-4 font-semibold uppercase tracking-wide">{block.title}</p>
                <p className="mt-2 text-sm leading-relaxed text-white/55">{block.text}</p>
              </div>
            ))}
          </div>

          <p className="mt-8 max-w-2xl text-sm leading-relaxed text-white/55 sm:text-base">
            {course.outro}
          </p>
        </section>

        {/*
          Ссылки между страницами нужны и человеку, и поиску: с A1 логично
          уйти на A, а с обеих — на главную, где расписание, карта и контакты.
        */}
        <section>
          <h2 className="text-2xl font-semibold uppercase tracking-tight sm:text-3xl">
            Другие занятия
          </h2>

          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            {COURSES.filter((item) => item.slug !== course.slug).map((item) => (
              <Link
                key={item.slug}
                to={`/${item.slug}`}
                className={cn(PANEL, 'block hover:border-accent/40')}
              >
                <p className="font-semibold uppercase tracking-wide">
                  {item.h1.plain} <span className="text-accent">{item.h1.accent}</span>
                </p>
                <p className="mt-2 text-sm leading-relaxed text-white/55">{item.short}</p>
              </Link>
            ))}

            <Link to="/" className={cn(PANEL, 'block hover:border-accent/40')}>
              <p className="font-semibold uppercase tracking-wide">
                О школе <span className="text-accent">и площадке</span>
              </p>
              <p className="mt-2 text-sm leading-relaxed text-white/55">
                Инструкторы, упражнения площадки, карта и ответы на частые вопросы.
              </p>
            </Link>
          </div>
        </section>
      </main>

      <ContactSheet school={school} isOpen={isContacting} onClose={() => setIsContacting(false)} />
    </div>
  );
}
